import { onValue, push, ref, remove, serverTimestamp, update } from "firebase/database";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { displayUserName, UserAvatar } from "../components/UserAvatar";
import { useAuth } from "../lib/auth";
import { realtimeDb } from "../lib/firebase";
import { getUser } from "../lib/data";
import { setSeo } from "../lib/seo";
import type { LiveChatRoom } from "../lib/types";
import { useAsync } from "../lib/useAsync";

export function Chat() {
  const { roomId } = useParams();
  if (roomId) return <ChatRoom roomId={roomId} />;

  const { user, login } = useAuth();
  const [rooms, setRooms] = useState<LiveChatRoom[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { data: profile } = useAsync(() => (user ? getUser(user.uid) : Promise.resolve(null)), [user?.uid]);
  const isAdmin = profile?.role === "ADMIN";

  useEffect(() => {
    setSeo({
      title: "Chat | My Local Forum",
      description: "Sign in to browse live local chat rooms.",
      path: "/chat",
      noindex: false
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    return onValue(
      ref(realtimeDb, "liveChatRooms"),
      (snapshot) => {
        const value = snapshot.val() || {};
        setRooms(Object.values(value));
      },
      (err) => setError(err.message)
    );
  }, [user]);

  return (
    <section className="page">
      <h1>Chat</h1>
      {!user ? (
        <div className="panel">
          <p>Live chat rooms use the same backend rules as the app and require sign in.</p>
          <button className="primary-button" onClick={login}>
            Sign in to view chat rooms
          </button>
        </div>
      ) : error ? (
        <div className="state">{error}</div>
      ) : (
        <>
          {isAdmin ? <RoomEditor /> : null}
          <div className="masonry">
            {rooms.map((room) => (
              <article className="card" key={room.id}>
                <Link to={`/chat/${room.id}`}>
                  <h2>{room.name}</h2>
                  {room.imageUrl ? <img src={room.imageUrl} alt="" loading="lazy" /> : null}
                  {room.description ? <p>{room.description}</p> : null}
                  <div className="meta">{room.lastMessage || "No messages yet"}</div>
                </Link>
                {isAdmin ? <RoomAdminActions room={room} /> : null}
              </article>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function RoomEditor({ room }: { room?: LiveChatRoom }) {
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") || "").trim();
    if (!name) return;
    const data = {
      id: room?.id || "",
      name,
      description: String(form.get("description") || "").trim(),
      imageUrl: String(form.get("imageUrl") || "").trim() || null,
      lastMessage: room?.lastMessage || "",
      lastMessageAt: room?.lastMessageAt || 0
    };
    if (room) {
      await update(ref(realtimeDb, `liveChatRooms/${room.id}`), data);
    } else {
      const roomRef = push(ref(realtimeDb, "liveChatRooms"));
      await update(roomRef, { ...data, id: roomRef.key, createdAt: serverTimestamp() });
    }
    window.location.reload();
  }

  return (
    <form className="panel form-grid" onSubmit={submit}>
      <h2>{room ? "Edit Chat Room" : "Add Chat Room"}</h2>
      <input name="name" placeholder="Room name" defaultValue={room?.name || ""} />
      <textarea name="description" placeholder="Description" defaultValue={room?.description || ""} />
      <input name="imageUrl" placeholder="Image URL" defaultValue={room?.imageUrl || ""} />
      <button className="primary-button" type="submit">
        {room ? "Update" : "Create"}
      </button>
    </form>
  );
}

function RoomAdminActions({ room }: { room: LiveChatRoom }) {
  return (
    <details className="admin-actions">
      <summary>Edit</summary>
      <RoomEditor room={room} />
      <button className="danger-button" onClick={() => remove(ref(realtimeDb, `liveChatRooms/${room.id}`))}>
        Delete Room
      </button>
    </details>
  );
}

function ChatRoom({ roomId }: { roomId: string }) {
  const { user, login } = useAuth();
  const { data: profile } = useAsync(() => (user ? getUser(user.uid) : Promise.resolve(null)), [user?.uid]);
  const [messages, setMessages] = useState<Array<{ id: string; text: string; senderId?: string; senderName?: string; senderProfilePictureUrl?: string }>>([]);

  useEffect(() => {
    setSeo({ title: "Chat Room | My Local Forum", path: `/chat/${roomId}`, noindex: true });
  }, [roomId]);

  useEffect(() => {
    if (!user) return;
    return onValue(ref(realtimeDb, `liveChatMessages/${roomId}`), (snapshot) => {
      const value = snapshot.val() || {};
      setMessages(
        Object.entries(value).map(([id, data]) => ({
          id,
          ...(data as { text: string; senderId?: string; senderName?: string; senderProfilePictureUrl?: string })
        }))
      );
    });
  }, [roomId, user]);

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) return;
    const form = new FormData(event.currentTarget);
    const text = String(form.get("text") || "").trim();
    if (!text) return;
    await push(ref(realtimeDb, `liveChatMessages/${roomId}`), {
      roomId,
      senderId: user.uid,
      senderName: displayUserName(profile, user.displayName || user.email || "Forum user"),
      senderProfilePictureUrl: profile?.profilePictureUrl || user.photoURL || "",
      text,
      timestamp: serverTimestamp()
    });
    await update(ref(realtimeDb, `liveChatRooms/${roomId}`), {
      lastMessage: text,
      lastMessageAt: serverTimestamp()
    });
    event.currentTarget.reset();
  }

  return (
    <section className="page detail">
      <Link className="text-link" to="/chat">
        Back to chat
      </Link>
      <h1>Chat Room</h1>
      {!user ? (
        <Link className="primary-button" to="/auth">Sign in to chat</Link>
      ) : (
        <>
          <div className="message-list">
            {messages.map((message) => (
              <article className="comment" key={message.id}>
                <Link className="message-author" to={message.senderId ? `/users/${encodeURIComponent(message.senderId)}` : "/profile"}>
                  <UserAvatar name={message.senderName || "Forum user"} imageUrl={message.senderProfilePictureUrl} size="sm" />
                  <strong>{message.senderName || "Forum user"}</strong>
                </Link>
                <p>{message.text}</p>
              </article>
            ))}
          </div>
          <form className="form-grid" onSubmit={send}>
            <input name="text" placeholder="Message" />
            <button className="primary-button" type="submit">
              Send
            </button>
          </form>
        </>
      )}
    </section>
  );
}
